import {
  AMOUNT_ZERO,
  DashboardMarket,
  DashboardMarketAsset,
  DepositType,
  ProtocolKey,
} from "@silo/types";
import { getAssets } from "@silo/utils";
import { Array, Option, pipe } from "effect";
import produce from "immer";
import { match } from "ts-pattern";
import { DebtState } from "./types";
import { getProtocolConfig } from "@silo/config";

export function getDepositAssets(
  market: DashboardMarket
): DashboardMarketAsset[] {
  const depositAssets = pipe(
    getAssets(market),
    Array.flatMap((asset) => {
      const depositType = getDepositType(asset);

      /**
       * In dual collateral cases, need to effectively split the asset into two separate assets that each have borrowable and protected balances
       */
      return match(depositType)
        .with("dual", () => [
          produce(asset, (draft) => {
            draft.protectedBalance = AMOUNT_ZERO;
          }),
          produce(asset, (draft) => {
            draft.collateralBalance = AMOUNT_ZERO;
          }),
        ])
        .with("collateral", () => [asset])
        .with("protected", () => [asset])
        .with("none", () => [])
        .exhaustive();
    })
  );

  return depositAssets;
}

export function getDepositType(asset: DashboardMarketAsset): DepositType {
  const { collateralBalance, protectedBalance } = asset;

  if (collateralBalance > 0n && protectedBalance > 0n) {
    return "dual";
  }

  if (collateralBalance > 0n) return "collateral";

  if (protectedBalance > 0n) return "protected";

  return "none";
}

export function getIsCollateralAsset({
  collateralBalance,
}: DashboardMarketAsset): boolean {
  return collateralBalance > 0n;
}

export function getIsProtectedAsset({
  protectedBalance,
}: DashboardMarketAsset): boolean {
  return protectedBalance > 0n;
}

export function getIsDebtAsset({ debtBalance }: DashboardMarketAsset): boolean {
  return debtBalance > 0n;
}

export function getDebtAssets(market: DashboardMarket): DashboardMarketAsset[] {
  return pipe(getAssets(market), Array.filter(getIsDebtAsset));
}

export function getIsDepositAsset(asset: DashboardMarketAsset): boolean {
  return getIsCollateralAsset(asset) || getIsProtectedAsset(asset);
}

export function getIsBorrowingDisabled(market: DashboardMarket): boolean {
  return pipe(getAssets(market), Array.every(getIsDepositAsset));
}

export function getDebtState(market: DashboardMarket): DebtState {
  const isBorrowingDisabled = getIsBorrowingDisabled(market);

  if (isBorrowingDisabled) {
    return { _tag: "disabled" };
  }

  const debtAssets = getDebtAssets(market);

  if (debtAssets.length === 0) {
    return { _tag: "none" };
  }

  return { _tag: "hasDebt", debtAssets };
}

export function getIsBorrowableAsset(asset: DashboardMarketAsset): boolean {
  return !getIsDepositAsset(asset) && asset.depositMode !== "protectedOnly";
}

export function getBorrowableAssets(
  market: DashboardMarket
): DashboardMarketAsset[] {
  return pipe(getAssets(market), Array.filter(getIsBorrowableAsset));
}

export function getNativeRewardsSymbol(protocolKey: ProtocolKey): string {
  const nativeRewardsSymbol = pipe(
    getProtocolConfig(protocolKey),
    (config) => config.nativeRewards,
    Option.fromNullable,
    Option.map((nativeRewards) => nativeRewards.symbol),
    Option.getOrElse(() => "")
  );

  return nativeRewardsSymbol;
}
